/**
 * The Errors tab (user, 19/10/2026: "every error traced, every error on
 * Slack, visualised professionally"): one card — the head says how many
 * today, how many open, the last one, and whether every error reaches
 * Slack; the range (today · 7 days · 30 days); the tiles; the chart per
 * hour or per day; the kinds as filter chips; then every error as a row
 * that opens on click. Degrades honestly: a failed read offers Retry, no
 * error says so.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import type { DropGenErrorsReport } from '@trafficpulse/shared';

import { Badge, Card, HintDot, SegmentedPills, Tooltip } from '@/components/ui';

import { instantText } from '../../utils/clock/dk-label';
import { useCountryClock } from '../shared/time/country-clock-context';
import { SectionAction } from '../timeline/section/SectionAction';
import { ErrorsChart } from './chart/ErrorsChart';
import { type ErrorFacts, errorFacts, filterRows, scoped } from './errors-facts';
import { slackWords } from './errors-words';
import { ErrorsTable } from './table/ErrorsTable';
import { KindChips } from './table/KindChips';
import { ErrorTiles } from './tiles/ErrorTiles';

const HINT =
  'Every error the automation hits, kept: a send SMSWiser refused, a send whose outcome is unknown, a halt, a day not laid out, texts not filled, a verdict or scaling step that failed, a tracker or stats read that failed, an alert not delivered. Each row says where, the exact message, what the automation did next, whether Slack heard it, and whether it is resolved — texts filled, a refused drop a later attempt sent, a step that ran again (open ones stay on top). Every one goes to the country’s Slack thread while the Errors topic is on.';

const RANGES = [
  { value: '1', label: 'Today' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
] as const;

interface Props {
  report: DropGenErrorsReport | undefined;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  days: number;
  onDays: (days: number) => void;
  scopeIds?: ReadonlySet<string> | null;
  scopeLabel?: string | null;
  onEditContainer?: (containerId: string) => void;
}

/** "3 errors today · 1 open · last 14:47 🇩🇰 · 🇦🇺 23:47". */
export const headline = (f: ErrorFacts, clock: Parameters<typeof instantText>[1]): string =>
  [
    `${f.today} error${f.today === 1 ? '' : 's'} today${f.happened > f.today ? ` (happened ×${f.happened})` : ''}`,
    f.open > 0 ? `${f.open} open` : null,
    f.resolved > 0 ? `${f.resolved} resolved` : null,
    f.last ? `last ${instantText(f.last.at, clock)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

const SlackChip: FC<{ state: DropGenErrorsReport['slack'] }> = ({ state }) => {
  const w = slackWords(state);
  return (
    <Tooltip content={w.hint}>
      <Badge tone={w.tone}>{w.label}</Badge>
    </Tooltip>
  );
};

export const ErrorsTabCard: FC<Props> = ({ report, loading, failed, onRetry, days, onDays, scopeIds = null, scopeLabel = null, onEditContainer }) => {
  const [kinds, setKinds] = useState<ReadonlySet<string>>(new Set());
  const clock = useCountryClock();
  const rows = useMemo(() => scoped(report?.rows ?? [], scopeIds), [report, scopeIds]);
  const facts = report ? errorFacts(report, rows, clock.tz) : null;
  const toggleKind = (kind: string) =>
    setKinds((prev) => {
      const next = new Set(prev);
      if (!next.delete(kind)) next.add(kind);
      return next;
    });
  return (
    <Card className="overflow-hidden" data-testid="errors-tab">
      <div className="flex flex-wrap items-center gap-3 border-b border-border/40 px-5 py-3">
        <ShieldAlert className="h-4 w-4 text-primary" aria-hidden />
        <span className="text-sm font-semibold">Errors{report ? ` — ${days === 1 ? `${report.to.slice(8, 10)}/${report.to.slice(5, 7)}` : `last ${days} days`}` : ''}</span>
        <HintDot label={HINT} />
        {report ? <SlackChip state={report.slack} /> : null}
        <span className="ml-auto font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {facts ? headline(facts, clock) : ''}
          {scopeLabel ? ` · ${scopeLabel} only` : ''}
        </span>
        <SegmentedPills options={RANGES} value={String(days) as (typeof RANGES)[number]['value']} onChange={(v) => onDays(Number(v))} />
      </div>
      {failed && !report ? (
        <div className="flex flex-col items-start gap-2 p-6">
          <p className="text-sm text-muted-foreground">the error trace is unavailable</p>
          <SectionAction onClick={onRetry}>Retry</SectionAction>
        </div>
      ) : loading && !report ? (
        <div className="flex flex-col gap-1.5 p-5">
          {[0, 1, 2].map((i) => <div key={i} className="h-9 animate-pulse rounded-md bg-muted/60" />)}
        </div>
      ) : report && facts && rows.length === 0 ? (
        <div className="flex flex-col items-start gap-1 p-6" data-testid="errors-empty">
          <p className="text-sm font-medium">No error {days === 1 ? 'today' : `in the last ${days} days`}.</p>
          <p className="max-w-xl text-sm text-muted-foreground">
            {report.slack.on ? 'Every error the automation hits lands here and in the country’s Slack thread.' : `Every error the automation hits lands here. ${report.slack.why}`}
          </p>
        </div>
      ) : report && facts ? (
        <>
          <ErrorTiles facts={facts} />
          <ErrorsChart report={report} rows={rows} days={days} tz={clock.tz} />
          <KindChips rows={rows} kinds={kinds} onToggle={toggleKind} onClear={() => setKinds(new Set())} />
          <ErrorsTable rows={filterRows(rows, kinds)} report={report} days={days} onEditContainer={onEditContainer} />
          <p className="border-t border-border/40 px-5 py-2.5 text-[11px] text-muted-foreground">
            Click a row for what happened, why, and what to do. Every row is kept; “33 times” means the same error came back 33 times within 30 minutes. An error turns ✓ Resolved once it is fixed — the texts filled, a refused drop sent by a later attempt, a step or a sync that ran again, a halt resumed — and sinks under the open ones of its day; Slack hears it as a ✅ line (a refused drop that went out, as its sent line). A read that failed, a hit-again not laid and a bookkeeping slip stay as history. Read failures and a failed alert stay here only — every other error goes to Slack as a ⛔ line.
          </p>
        </>
      ) : null}
    </Card>
  );
};
